import { z } from 'zod';

// ── Payload shapes (kept in jsonb on the DB side) ─────────────────────────

const StatusItem = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
  category: z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']),
  order: z.number().int(),
});
const PriorityItem = z.object({
  id: z.string(),
  name: z.string(),
  level: z.number().int().min(1).max(4),
  color: z.string(),
});
const ChecklistTpl = z.object({
  name: z.string(),
  items: z.array(z.string()),
});
const InitialTask = z.object({
  title: z.string(),
  description: z.string().optional(),
  type: z.string().optional(),
  priority: z.number().int().min(1).max(4).optional(),
  statusId: z.string().optional(),
  checklists: z.array(ChecklistTpl).optional(),
});
const MilestoneTpl = z.object({
  name: z.string(),
  color: z.string().optional(),
  plannedEndOffsetDays: z.number().int().optional(),
});
/** Project-scoped label seeded by the template (Sprint 2.2).  English names. */
const TagTpl = z.object({
  name: z.string(),
  color: z.string(),
  description: z.string().optional(),
});

export const ProjectTemplatePayloadSchema = z.object({
  statuses: z.array(StatusItem).default([]),
  priorities: z.array(PriorityItem).default([]),
  initialTasks: z.array(InitialTask).default([]),
  milestones: z.array(MilestoneTpl).default([]),
  tags: z.array(TagTpl).default([]),
});
export type ProjectTemplatePayload = z.infer<typeof ProjectTemplatePayloadSchema>;

export const TaskTemplatePayloadSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  type: z.string().optional(),
  priority: z.number().int().min(1).max(4).optional(),
  checklists: z.array(ChecklistTpl).optional(),
});
export type TaskTemplatePayload = z.infer<typeof TaskTemplatePayloadSchema>;

// ── Projections ───────────────────────────────────────────────────────────

export const ProjectTemplateSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid().nullable(),
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string().nullable(),
  payload: ProjectTemplatePayloadSchema,
  isSystem: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProjectTemplateProjection = z.infer<typeof ProjectTemplateSchema>;

export const ProjectTemplateListSchema = z.array(ProjectTemplateSchema);

export const TaskTemplateSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid().nullable(),
  slug: z.string(),
  name: z.string(),
  payload: TaskTemplatePayloadSchema,
  isSystem: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TaskTemplateProjection = z.infer<typeof TaskTemplateSchema>;

export const TaskTemplateListSchema = z.array(TaskTemplateSchema);

// ── Apply template inputs ─────────────────────────────────────────────────

export const ApplyProjectTemplateSchema = z
  .object({
    templateId: z.string().uuid(),
    name: z.string().min(1).max(120),
    slug: z.string().min(1).max(60).regex(/^[a-z0-9-]+$/),
    key: z.string().min(2).max(8).regex(/^[A-Z]+$/),
    icon: z.string().optional(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  })
  .strict();
export type ApplyProjectTemplateInput = z.infer<typeof ApplyProjectTemplateSchema>;

// ── Create / update (team templates only) ─────────────────────────────────

export const CreateProjectTemplateSchema = z
  .object({
    slug: z.string().min(1).max(60).regex(/^[a-z0-9-]+$/),
    name: z.string().min(1).max(120),
    description: z.string().max(400).optional(),
    icon: z.string().max(40).optional(),
    payload: ProjectTemplatePayloadSchema,
  })
  .strict();
export type CreateProjectTemplateInput = z.infer<typeof CreateProjectTemplateSchema>;
